import * as Yup from "yup";
import { StockStrategy } from "kadow-jetpack-model";
import { FIELD_REQUIRED, PERCENTAGE } from "../../../../../utils/constants";
import { type FormikErrors } from "formik";
import { tt } from "../../../../../utils/i18n/i18n";

const WRITTEN_NUMBERS_EN = [
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
  "twenty"
];

const WRITTEN_NUMBERS_FR = [
  "un",
  "deux",
  "trois",
  "quatre",
  "cinq",
  "sept",
  "huit",
  "neuf",
  "dix",
  "onze",
  "douze",
  "treize",
  "quatorze",
  "quinze",
  "seize",
  "dix-sept",
  "dix-huit",
  "dix-neuf",
  "vingt"
];

const PRIZE_NAME_REGEX = new RegExp(
  `^(\\d+|${[...WRITTEN_NUMBERS_EN, ...WRITTEN_NUMBERS_FR].join("|")})`,
  "i"
);

const PRIZE_NAME_MIN_CHARACTERS = 5;
const PRIZE_NAME_MAX_CHARACTERS = 25;

export const giftNameValidation = Yup.string()
  .required(tt("gift_name_required"))
  .min(PRIZE_NAME_MIN_CHARACTERS, tt("gift_name_min_characters"))
  .matches(PRIZE_NAME_REGEX, tt("gift_name_must_start_with_number"))
  .max(
    PRIZE_NAME_MAX_CHARACTERS,
    tt("prize_name_max_characters", { count: PRIZE_NAME_MAX_CHARACTERS })
  );

export default Yup.object({
  prizes: Yup.array()
    .of(
      Yup.object().shape({
        name: giftNameValidation,
        image: Yup.string().required(FIELD_REQUIRED),
        pc: Yup.number()
          // transform the value to 0 to avoid the case NaN error (must be a `number` type, but the final value was: `NaN`)
          .transform((value) => (isNaN(value) ? 0 : value))
          .min(0, PERCENTAGE)
          .max(100, PERCENTAGE)
          .required(FIELD_REQUIRED),
        conditions: Yup.array().of(Yup.string().optional()),
        iQty: Yup.number().when("ss", {
          is: (ss: string) => ss !== StockStrategy.None,
          then: (schema) => schema.min(1, FIELD_REQUIRED).required(FIELD_REQUIRED)
        }),
        expiry: Yup.object().optional()
      })
    )
    .test("sum_of_prizes_pc", "dummy", (value, { createError }) => {
      let sum = 0;
      value != null &&
        value.forEach((prize) => {
          return (sum += prize.pc || 0);
        });

      if (sum > 100) {
        return createError({
          path: "prizes",
          message: tt("gift_percentage_sum_exceed", { sum })
        });
      }

      return true;
    })
});

export function flattenErrors(obj: FormikErrors<any>, path = ""): string[] {
  return Object.entries(obj).reduce((acc: string[], [key, value]) => {
    const currentPath = path ? `${path}.${key}` : key;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === "object" && item !== null) {
          acc.push(...flattenErrors(item, `${currentPath}[${index}]`));
        } else if (item) {
          acc.push(`${currentPath}[${index}]: ${item}`);
        }
      });
    } else if (typeof value === "object" && value !== null) {
      acc.push(...flattenErrors(value, currentPath));
    } else if (value) {
      acc.push(`${value}`);
    }
    return acc;
  }, []);
}
